import { useContext } from 'react'

import { faDownload, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'
import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, Form, ListItem, ModalContext, SidebarModal, StatusBadge, Text,
} from '@domain-group/suit-theme'

import useCurrentUser from '@hooks/useCurrentUser'
import useCampaignPortalSyncDetailsModal from './hooks/useCampaignPortalSyncDetailsModal'

const MODAL_KEY = 'CampaignPortalSyncDetailsModal'

export type ModalCallbacks = {
  closeModal: () => void,
  fetchLog: (fetchLogPayload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
}

const CampaignPortalSyncDetailsModal = (props: ModalProps) => {
  const {
    callbacks, requestOptions, showModal,
  } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaignPortalSync } = modalPayload

  const { campaignPortalSyncPayload } = useCampaignPortalSyncDetailsModal({
    callbacks, campaignPortalSync, requestOptions,
  })

  const { isAdmin } = useCurrentUser()

  const {
    campaignPortalSync: {
      // @ts-expect-error
      file,
      log_data,
      identity_code,
      portal_id,
      success,
      sync_type,
      unique_id,
    },
    errorMessage,
    lastSyncDateStr,
    listingId,
    portal,
  } = campaignPortalSyncPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Portal Sync" titleSecondLine="Details" />

      <SidebarModal.Content>
        {errorMessage && (
          <Box flexDirection="column" lineHeight="1.3" marginBottom="large" padding="large" variant="whiteGrey">
            <Text color="orange" fontSize="small">
              <FontAwesomeIcon icon={faExclamationTriangle} /> Errors with Portal Sync.
            </Text>

            <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="large">
              {errorMessage}
            </Text>
          </Box>
        )}

        <Form.Field direction="column" label="Sync Details">
          {/*
           // @ts-expect-error TS2339 */}
          <ListItem.Compact
            boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
            contentRight={sync_type}
            disableHover
            showAngleIcon={false}
          >
            Sync Type
            {/*
            // @ts-expect-error TS2339 */}
          </ListItem.Compact>

          {/*
           // @ts-expect-error TS2339 */}
          <ListItem.Compact
            boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
            contentRight={portal.title || `Portal #${portal_id}`}
            disableHover
            showAngleIcon={false}
          >
            Portal
            {/*
            // @ts-expect-error TS2339 */}
          </ListItem.Compact>

          {/*
           // @ts-expect-error TS2339 */}
          <ListItem.Compact
            boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
            contentRight={(
              <StatusBadge
                // @ts-expect-error
                key={success}
                color={success ? 'green' : 'orange'}
                ghost
                text={success ? 'SUCCESS' : 'FAIL'}
              />
            )}
            disableHover
            showAngleIcon={false}
          >
            Status
            {/*
            // @ts-expect-error TS2339 */}
          </ListItem.Compact>

          {/*
           // @ts-expect-error TS2339 */}
          <ListItem.Compact
            boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
            contentRight={lastSyncDateStr || 'Not Synced'}
            disableHover
            showAngleIcon={false}
          >
            Synced At
            {/*
            // @ts-expect-error TS2339 */}
          </ListItem.Compact>

          {/*
           // @ts-expect-error TS2339 */}
          <ListItem.Compact
            boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
            contentRight={identity_code}
            disableHover
            showAngleIcon={false}
          >
            Identity Code
            {/*
            // @ts-expect-error TS2339 */}
          </ListItem.Compact>

          {/*
           // @ts-expect-error TS2339 */}
          <ListItem.Compact
            boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
            contentRight={unique_id}
            disableHover
            showAngleIcon={false}
          >
            Unique ID
            {/*
            // @ts-expect-error TS2339 */}
          </ListItem.Compact>

          {listingId && (
            // @ts-expect-error TS2339
            <ListItem.Compact
              boxProps={{ borderBottom: '1px dashed', paddingX: 0 }}
              contentRight={listingId}
              disableHover
              showAngleIcon={false}
            >
              Listing Id
              {/*
             // @ts-expect-error TS2339 */}
            </ListItem.Compact>
          )}

          {!!log_data && isAdmin && (
            <Form.Field
              label="Log Data"
              marginTop="large"
            >
              <textarea
                disabled
                style={{ height: 120, resize: 'vertical' }}
                value={JSON.stringify(log_data, null, 4)}
              />
            </Form.Field>
          )}

          {!!file && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                onClick={() => window.open(file, '_blank')}
                icon={<FontAwesomeIcon icon={faDownload} />}
                size="medium"
              >
                Download File
              </Button>
            </Form.Field>
          )}
        </Form.Field>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CampaignPortalSyncDetailsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
